/**
 * Section: Tw video cards
 * -----------------------------------------------------------------------------
 * Play and pause button for a video card. Starting a video pauses any other
 * video in the same section, the button resets when a video ends.
 * Wrapped in a guard so a second copy of the section doesn't redefine it.
 *
 */

if (!customElements.get('tw-video-card')) {

  /**
   * Custom element wrapping one video and its toggle button.
   */
  class TwVideoCard extends HTMLElement {

    /**
     * Cache elements and bind listeners.
     */
    connectedCallback() {
      this.video = this.querySelector('[js-video="media"]')
      this.toggle = this.querySelector('[js-video="toggle"]')

      if (!this.video || !this.toggle) {
        return
      }

      this.handleClick = this.handleClick.bind(this)
      this.sync = this.sync.bind(this)

      this.toggle.addEventListener('click', this.handleClick)
      this.video.addEventListener('play', this.sync)
      this.video.addEventListener('pause', this.sync)
      this.video.addEventListener('ended', this.sync)
    }

    /**
     * Remove listeners when the section is removed in the theme editor.
     */
    disconnectedCallback() {
      if (!this.video || !this.toggle) {
        return
      }

      this.toggle.removeEventListener('click', this.handleClick)
      this.video.removeEventListener('play', this.sync)
      this.video.removeEventListener('pause', this.sync)
      this.video.removeEventListener('ended', this.sync)
    }

    /**
     * Play or pause the video, pausing the others in the section first.
     */
    handleClick() {
      if (!this.video.paused) {
        this.video.pause()

        return
      }

      const section = this.closest('.tw-video-cards') || document

      section.querySelectorAll('tw-video-card').forEach((card) => {
        if (card !== this && card.video && !card.video.paused) {
          card.video.pause()
        }
      })

      this.video.play().catch(() => this.sync())
    }

    /**
     * Mirror the playing state on the button.
     */
    sync() {
      const playing = !this.video.paused && !this.video.ended

      this.toggle.setAttribute('aria-pressed', playing ? 'true' : 'false')
      this.toggleAttribute('data-playing', playing)
    }
  }

  customElements.define('tw-video-card', TwVideoCard)
}
